import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Probability, Random Variables, and Probability Distributions",
  examWeight: "10–20%",
  focus: "Probability as the Foundation for Statistical Inference",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Introducing Statistics: Random and Non-Random Patterns?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Nature of Randomness</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In statistics, <strong>random</strong> does not mean "haphazard." It describes a phenomenon where individual outcomes are uncertain, but there is a regular distribution of outcomes in a large number of repetitions.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Short-run</h4>
                <p className="text-sm text-slate-700 mt-1">Unpredictable (e.g., flipping a coin once).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Long-run</h4>
                <p className="text-sm text-slate-700 mt-1">Predictable pattern (e.g., flipping a coin 10,000 times will result in approx. 50% heads).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Probability Definition</h3>
            <KeyTerm 
              term="Probability" 
              definition="The probability of any outcome of a chance process is a number between 0 and 1 that describes the proportion of times the outcome would occur in a very long series of repetitions."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Law of Large Numbers (LLN)</h3>
            <KeyTerm 
              term="Law of Large Numbers" 
              definition="If we do something many, many times, the proportion of desired outcomes will approach its true probability."
            />
            <ImportantNote>
              <strong>Misconception Alert:</strong> The "Law of Averages" is a myth. If you flip 5 heads in a row, the coin is not "due" for a tail. Independent events have no memory. The LLN works by <em>diluting</em> early streaks over a massive number of trials, not by <em>compensating</em> for them in the short run.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Estimating Probabilities Using Simulation",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            Simulation is the imitation of chance behavior, based on a model that accurately reflects the situation.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The 4-Step Process for Simulation</h3>
            <ol className="list-decimal pl-6 space-y-4 text-slate-700">
              <li>
                <strong>State:</strong> Ask a question about some chance process.
              </li>
              <li>
                <strong>Plan:</strong> Describe how to use a chance device to imitate one trial. You must clearly identify:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong>Component:</strong> What represents one single action?</li>
                  <li><strong>Outcome assignment:</strong> What numbers/results represent success vs. failure?</li>
                  <li><strong>Repeats:</strong> Are repeats allowed? (Sampling with vs. without replacement).</li>
                  <li><strong>Stopping rule:</strong> When does one trial end?</li>
                  <li><strong>Response variable:</strong> What are you recording at the end of the trial?</li>
                </ul>
              </li>
              <li><strong>Do:</strong> Perform many repetitions (trials) of the simulation.</li>
              <li><strong>Conclude:</strong> Use the results to answer the question.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Tools</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Coins/Dice:</strong> Good for 50/50 or 1/6 probabilities.</li>
              <li><strong>Random Number Table/Generator:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><em>Example:</em> Probability of 20%. Assign digits 0, 1 as "Success" and 2–9 as "Failure."</li>
                  <li><em>Crucial Detail:</em> If selecting people, ignore repeated numbers (cannot select the same person twice).</li>
                </ul>
              </li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Introduction to Probability",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sample Space and Events</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm 
                term="Sample Space (S)" 
                definition="The set of all possible outcomes."
              />
              <KeyTerm 
                term="Event" 
                definition="A subset of the sample space (usually denoted by capital letters, e.g., A)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Basic Probability Rules</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Range</h4>
                <p className="text-sm text-slate-700 mt-1">For any event <InlineMath math="A" />:</p>
                <div className="mt-2"><BlockMath math="0 \le P(A) \le 1" /></div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">2. Sum</h4>
                <p className="text-sm text-slate-700 mt-1">The sum of probabilities of all possible outcomes must equal 1:</p>
                <div className="mt-2"><BlockMath math="P(S) = 1" /></div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Complement Rule</h4>
                <p className="text-sm text-slate-700 mt-1">The probability that an event does <em>not</em> occur is 1 minus the probability that it <em>does</em> occur:</p>
                <div className="mt-2"><BlockMath math="P(A^c) = 1 - P(A)" /></div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Mutually Exclusive Events",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Mutually Exclusive (Disjoint)" 
              definition="Two events that have no outcomes in common and can never occur together."
            />
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><em>Visual:</em> In a Venn diagram, the circles do not overlap.</li>
              <li><em>Mathematical notation:</em> <InlineMath math="P(A \cap B) = 0" /> (The probability of A <em>and</em> B is zero).</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Addition Rule for Mutually Exclusive Events</h3>
            <p className="text-slate-700 leading-relaxed mb-4">If <InlineMath math="A" /> and <InlineMath math="B" /> are disjoint:</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="P(A \text{ or } B) = P(A \cup B) = P(A) + P(B)" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Conditional Probability",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Conditional Probability" 
              definition="The probability that one event happens given that another event is already known to have happened."
            />
            <p className="text-slate-700 mt-4"><strong>Notation:</strong> <InlineMath math="P(A | B)" /> reads "Probability of A given B."</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="P(A | B) = \frac{P(A \cap B)}{P(B)}" />
            </div>
            <p className="text-slate-700">
              <em>Logic:</em> Since we know <InlineMath math="B" /> happened, the sample space shrinks to only the outcomes in <InlineMath math="B" />. We look for the outcomes of <InlineMath math="A" /> that are <em>inside</em> this new sample space.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculating from Tables</h3>
            <p className="text-slate-700">
              In a two-way table, conditional probability usually restricts your view to a single row or a single column (the denominator) rather than the table total.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Independent Events and Unions of Events",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The General Addition Rule (Union)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Used for <strong>any</strong> two events (disjoint or not). We must subtract the overlap to avoid double-counting.</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="P(A \text{ or } B) = P(A) + P(B) - P(A \text{ and } B)" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Independence</h3>
            <KeyTerm 
              term="Independent Events" 
              definition="Two events A and B are independent if the occurrence of one event does not change the probability that the other event will happen."
            />
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
              <h4 className="font-bold text-green-900 mb-2">Tests for Independence (show one to prove independence)</h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><InlineMath math="P(A | B) = P(A)" /></li>
                <li><InlineMath math="P(B | A) = P(B)" /></li>
                <li><InlineMath math="P(A \cap B) = P(A) \cdot P(B)" /></li>
              </ol>
            </div>

            <ImportantNote>
              <strong>Warning:</strong> Do not confuse "Mutually Exclusive" with "Independent."
              <p className="mt-2">If two events are Mutually Exclusive (and <InlineMath math="P > 0" />), they are <strong>Dependent</strong>. (If I know outcome A happened, I know outcome B represents 0% chance).</p>
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The General Multiplication Rule (Intersection)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">For any two events:</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="P(A \cap B) = P(A) \cdot P(B | A)" />
            </div>
            <p className="text-slate-700"><em>If Independent:</em> <InlineMath math="P(A \cap B) = P(A) \cdot P(B)" /></p>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Intro to Random Variables and Probability Distributions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Random Variable (RV)</h3>
            <KeyTerm 
              term="Random Variable" 
              definition="A variable whose value is a numerical outcome of a random phenomenon. Usually denoted by X."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Discrete Random Variable</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Takes a fixed set of possibilities with gaps (countable).</li>
              <li><em>Example:</em> Number of heads in 3 coin flips (<InlineMath math="0, 1, 2, 3" />).</li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900">Distribution Requirements</h4>
              <p className="text-sm text-slate-700 mt-1">A list of values (<InlineMath math="x_i" />) and their probabilities (<InlineMath math="p_i" />):</p>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                <li>Every <InlineMath math="p_i" /> is between 0 and 1</li>
                <li>Sum of <InlineMath math="p_i = 1" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Continuous Random Variable</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Takes all values in an interval of numbers (measurable).</li>
              <li><em>Example:</em> Height of students, time to run a mile.</li>
              <li><strong>Distribution:</strong> Described by a density curve (like the Normal curve). Probability is the <strong>area</strong> under the curve.</li>
            </ul>
            <ImportantNote>
              <InlineMath math="P(X = \text{exact number}) = 0" /> for continuous variables. We only calculate probabilities for ranges, e.g., <InlineMath math="P(X < 5)" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-8",
      title: "Topic 4.8: Mean and Standard Deviation of Random Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Mean (Expected Value)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">The "balance point" of the probability distribution. It represents the theoretical long-run average.</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="\mu_X = E(X) = \sum x_i p_i" />
              <p className="text-sm text-slate-600 mt-2"><em>Multiply every outcome by its probability and sum them up.</em></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Variance and Standard Deviation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Measures the "spread" or variability.</p>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Variance</h4>
                <BlockMath math="\sigma_X^2 = \sum (x_i - \mu_X)^2 p_i" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Standard Deviation</h4>
                <BlockMath math="\sigma_X = \sqrt{\text{Variance}}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpretation</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>"If we were to repeat this random process many, many times, the average value of <InlineMath math="X" /> would be approximately <InlineMath math="\mu_X" />."</li>
              <li>"On average, a randomly selected value of <InlineMath math="X" /> will differ from the mean <InlineMath math="\mu_X" /> by about <InlineMath math="\sigma_X" />."</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-9",
      title: "Topic 4.9: Combining Random Variables",
      content: (
        <div className="space-y-6">
          <ImportantNote>
            This is a <strong>high-frequency exam topic</strong>.
          </ImportantNote>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Linear Transformations</h3>
            <p className="text-slate-700 leading-relaxed mb-4">If <InlineMath math="Y = a + bX" /> (adding a constant <InlineMath math="a" /> and multiplying by <InlineMath math="b" />):</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Mean</h4>
                <BlockMath math="\mu_Y = a + b(\mu_X)" />
                <p className="text-sm text-slate-600 mt-2">Affected by both addition and multiplication.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Standard Deviation</h4>
                <BlockMath math="\sigma_Y = |b|(\sigma_X)" />
                <p className="text-sm text-slate-600 mt-2">Affected ONLY by multiplication/scaling. Adding a constant does not change the spread.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Combining Different Variables (Sums and Differences)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Let <InlineMath math="X" /> and <InlineMath math="Y" /> be random variables:</p>
            
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">Means (Always apply)</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>Mean of Sum: <InlineMath math="\mu_{X+Y} = \mu_X + \mu_Y" /></div>
                  <div>Mean of Difference: <InlineMath math="\mu_{X-Y} = \mu_X - \mu_Y" /></div>
                </div>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900 mb-2">Crucial Condition for Variance/SD</h4>
                <p className="text-sm text-slate-700">The following rules only apply if <InlineMath math="X" /> and <InlineMath math="Y" /> are <strong>INDEPENDENT</strong>:</p>
                <div className="mt-3 space-y-2">
                  <p>Variance of Sum: <InlineMath math="\sigma_{X+Y}^2 = \sigma_X^2 + \sigma_Y^2" /></p>
                  <p>Variance of Difference: <InlineMath math="\sigma_{X-Y}^2 = \sigma_X^2 + \sigma_Y^2" /></p>
                </div>
              </div>
            </div>

            <ImportantNote>
              Variances <strong>always add</strong>, even when subtracting variables. You cannot simply add standard deviations. You must square them (variance), add them, and take the square root:
              <div className="mt-2"><InlineMath math="\sigma_{X+Y} = \sqrt{\sigma_X^2 + \sigma_Y^2}" /></div>
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-10",
      title: "Topic 4.10: Introduction to the Binomial Distribution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The BINS Conditions</h3>
            <Mnemonic title="BINS" content="Binary, Independent, Number fixed, Same probability — the conditions for a Binomial setting." />
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>B</strong>inary: Outcomes are either Success or Failure.</li>
              <li><strong>I</strong>ndependent: Trials must be independent (knowing result of one doesn't affect the other).</li>
              <li><strong>N</strong>umber: The number of trials (<InlineMath math="n" />) is fixed in advance.</li>
              <li><strong>S</strong>uccess: The probability of success (<InlineMath math="p" />) is the same for each trial.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Binomial Coefficient</h3>
            <p className="text-slate-700 leading-relaxed mb-4"><InlineMath math="\binom{n}{k}" /> counts the number of ways to arrange <InlineMath math="k" /> successes among <InlineMath math="n" /> trials.</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\binom{n}{k} = \frac{n!}{k!(n-k)!}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Binomial Probability Formula</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="P(X = k) = \binom{n}{k} p^k (1-p)^{n-k}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculator Functions</h3>
            <div className="space-y-3">
              <div className="bg-slate-100 p-3 rounded border border-slate-300">
                <code className="font-mono text-sm">binompdf(n, p, k)</code>
                <p className="text-sm text-slate-600 mt-1">Probability of exactly <InlineMath math="k" /> successes.</p>
              </div>
              <div className="bg-slate-100 p-3 rounded border border-slate-300">
                <code className="font-mono text-sm">binomcdf(n, p, k)</code>
                <p className="text-sm text-slate-600 mt-1">Probability of <InlineMath math="k" /> or <em>fewer</em> successes (<InlineMath math="P(X \le k)" />).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-11",
      title: "Topic 4.11: Parameters for a Binomial Distribution",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            If <InlineMath math="X" /> is a binomial random variable with parameters <InlineMath math="n" /> and <InlineMath math="p" />:
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Mean (Expected Value)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="\mu_X = np" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Standard Deviation</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <BlockMath math="\sigma_X = \sqrt{np(1-p)}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sampling Without Replacement (10% Condition)</h3>
            <KeyTerm 
              term="10% Condition" 
              definition="Technically, sampling without replacement violates the 'Independence' condition of BINS. However, if the sample size (n) is less than 10% of the population size (N), we can treat the trials as independent for calculation purposes."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-4-12",
      title: "Topic 4.12: The Geometric Distribution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Setting</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Geometric distributions model the number of trials required to achieve the <strong>first</strong> success.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The BIT Conditions</h3>
            <Mnemonic title="BIT" content="Binary, Independent, Trials until first success — the conditions for a Geometric setting." />
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>B</strong>inary: Success/Failure.</li>
              <li><strong>I</strong>ndependent: Trials are independent.</li>
              <li><strong>T</strong>rials: We count trials <em>until</em> the first success. (Note: <InlineMath math="n" /> is not fixed).</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Probability Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">If <InlineMath math="X" /> is the number of trials to get the first success:</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="P(X=k) = (1-p)^{k-1}p" />
              <p className="text-sm text-slate-600 mt-2"><em>(Probability of failure <InlineMath math="k-1" /> times, followed by one success).</em></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parameters</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Mean (Expected Value)</h4>
                <BlockMath math="\mu = \frac{1}{p}" />
                <p className="text-sm text-slate-600 mt-2"><em>Example:</em> If you have a 1 in 5 chance of winning (<InlineMath math="p=0.2" />), you expect to play <InlineMath math="1/0.2 = 5" /> times to get your first win.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Standard Deviation</h4>
                <BlockMath math="\sigma = \frac{\sqrt{1-p}}{p}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculator Functions</h3>
            <div className="space-y-3">
              <div className="bg-slate-100 p-3 rounded border border-slate-300">
                <code className="font-mono text-sm">geometpdf(p, k)</code>
                <p className="text-sm text-slate-600 mt-1">Probability the first success is on exactly the <InlineMath math="k" />-th trial.</p>
              </div>
              <div className="bg-slate-100 p-3 rounded border border-slate-300">
                <code className="font-mono text-sm">geometcdf(p, k)</code>
                <p className="text-sm text-slate-600 mt-1">Probability the first success occurs on or before the <InlineMath math="k" />-th trial.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit4Notes;
